import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowUpRight, ChevronRight } from "lucide-react";
import { api, imgUrl } from "../api";
import ProductCard from "../components/ProductCard";
import Seo from "../components/Seo";

const FALLBACK_BG =
  "https://images.unsplash.com/photo-1610189019555-b1e26c2e424d?auto=format&fit=crop&w=2000&q=85";

export default function Category() {
  const { slug, categorySlug, subcategorySlug } = useParams();
  const requestedCategory = categorySlug || slug;
  const [category, setCategory] = useState(null);
  const [subcategory, setSubcategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setNotFound(false);
      setLoadError(false);
      try {
        const { data: tree } = await api.get("/api/categories/tree");
        const parent = tree.find((item) => item.slug === requestedCategory);
        const child = subcategorySlug
          ? parent?.children?.find((item) => item.slug === subcategorySlug)
          : null;
        if (!parent || (subcategorySlug && !child)) {
          setNotFound(true);
          return;
        }
        setCategory(parent);
        setSubcategory(child);
        const params = { category: parent.slug, limit: 200 };
        if (child) params.subcategory = child.slug;
        const { data } = await api.get("/api/products", { params });
        setProducts(Array.isArray(data) ? data : []);
      } catch {
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    })();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [requestedCategory, subcategorySlug]);

  if (notFound) {
    return (
      <main className="page">
        <p className="empty" data-testid="category-not-found">Category not found.</p>
        <div style={{ textAlign: "center" }}>
          <Link to="/shop" className="text-link">Browse all products <ArrowUpRight size={14} /></Link>
        </div>
      </main>
    );
  }

  const title = subcategory?.name || category?.name || requestedCategory;
  const bgImg = imgUrl(subcategory?.image || category?.image || FALLBACK_BG);
  const seoCategorySlug = category?.slug || requestedCategory;
  const seoSubcategorySlug = subcategory?.slug || subcategorySlug;
  const seoPath = seoSubcategorySlug
    ? `/shop/${seoCategorySlug}/${seoSubcategorySlug}`
    : `/shop/${seoCategorySlug}`;

  return (
    <main data-testid="category-page">
      <Seo
        title={`${title} Designer Wear in Hyderabad`}
        description={`Shop ${title} by Lalita Soujanya in Hyderabad. Explore premium Indian occasion wear with styling support across Telangana and Andhra Pradesh.`}
        path={seoPath}
        image={bgImg}
        schema={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: `${title} – Lalita Soujanya`,
          description: subcategory?.description || category?.description || `Explore the ${title} collection.`,
          url: `https://lalitasoujanya.com${seoPath}`,
        }}
      />
      <nav className="category-breadcrumb" aria-label="Breadcrumb">
        <Link to="/shop">Shop</Link><ChevronRight size={13} />
        {subcategory && category && <><Link to={`/shop/${category.slug}`}>{category.name}</Link><ChevronRight size={13} /></>}
        <span aria-current="page">{title}</span>
      </nav>
      <section className="category-hero" data-testid="category-hero">
        <div className="bg" style={{ backgroundImage: `url(${bgImg})` }} />
        <div className="content">
          <span className="eyebrow on-dark">{subcategory ? category?.name : "Shop category"}</span>
          <h1 data-testid="category-title">{title}</h1>
          <p>{subcategory?.description || category?.description || `Discover our ${title} collection.`}</p>
        </div>
      </section>
      <div className="container category-results">
        <div className="shop-toolbar">
          <span className="result-count" data-testid="category-result-count">
            {loading ? "Loading..." : `${products.length} products`}
          </span>
          <Link to="/shop" className="text-link">Full collection <ArrowUpRight size={14} /></Link>
        </div>
        {loading ? (
          <p className="empty">Waking our atelier and loading products...</p>
        ) : loadError ? (
          <div className="category-empty-state" data-testid="category-load-error">
            <h2>Still connecting.</h2>
            <p>The collection is taking longer than expected.</p>
            <button className="btn btn-outline" onClick={() => window.location.reload()}>Try again</button>
          </div>
        ) : products.length ? (
          <div className="product-grid" data-testid="category-grid">
            {products.map((product) => <ProductCard product={product} key={product.id} />)}
          </div>
        ) : (
          <div className="category-empty-state" data-testid="category-empty">
            <h2>Coming soon.</h2>
            <p>No products available in this collection yet. Check back soon!</p>
            <Link to="/shop" className="btn btn-outline">Browse all products</Link>
          </div>
        )}
      </div>
    </main>
  );
}
